import styled from '@emotion/styled';
import { createContext, useContext } from 'react';
import { useRef } from 'react';
import { useTable } from 'react-aria';
import { useTableState } from 'react-stately';

import { su } from '../../../theme/functions/su';
import { theme } from '../../../theme/theme';

import { SettingsTableColumnHeader } from './SettingsTableColumnHeader';
import { FamilySettingsTableHeaderRow } from './SettingsTableHeaderRow';
import { SettingsTableRow } from './SettingsTableRow';
import { SettingsTableRowGroup } from './SettingsTableRowGroup';

/**
 * @template {object} T
 * @typedef {object} RowContextValue
 * @property {import('@react-types/grid').GridNode<T>} row
 * @property {import('@react-stately/table').TableState<T>} state
 */

/**
 * @template {object} T
 * @typedef {object} CellContextValue
 * @property {import('@react-types/grid').GridNode<T>} row
 * @property {import('@react-types/grid').GridNode<T>} cell
 * @property {import('@react-stately/table').TableState<T>} state
 */

/**
 * @template {object} T
 * @typedef {object} HeaderColumnContextValue
 * @property {import('@react-types/grid').GridNode<T>} column
 * @property {import('@react-stately/table').TableState<T>} state
 */

/**
 * @template {object} T
 * @typedef {import('@react-types/table').TableProps<T> &
 *   import('react').HTMLAttributes<HTMLTableElement> & {
 *     isHeaderHidden?: boolean,
 *     isFullWidth?: boolean,
 *     renderRow?: (args: {
 *       children: import('react').ReactNode,
 *     }) => import('react').ReactNode,
 *     renderCell: (args: {
 *       cell: import('@react-types/grid').GridNode<T>,
 *       row: import('@react-types/grid').GridNode<T>,
 *     }) => import('react').ReactNode,
 *     renderColumnHeader?: (args: {
 *       column: import('@react-types/grid').GridNode<T>,
 *       state: import('@react-stately/table').TableState<T>,
 *     }) => import('react').ReactNode,
 *   }} SettingsTableProps
 */

/** @type {import('react').Context<RowContextValue<object> | null>} */
const RowContext = createContext(null);
/** @type {import('react').Context<CellContextValue<object> | null>} */
const CellContext = createContext(null);
/** @type {import('react').Context<HeaderColumnContextValue<object> | null>} */
const HeaderColumnContext = createContext(null);

/**
 * @template {object} T
 * @returns {RowContextValue<T>}
 */
export function useRowContext() {
  const context = useContext(RowContext);

  if (context == null) {
    throw new Error('useRowContext must be used within SettingsTable.');
  }

  return /** @type {RowContextValue<T>} */ (context);
}

/**
 * @template {object} T
 * @returns {CellContextValue<T>}
 */
export function useCellContext() {
  const context = useContext(CellContext);

  if (context == null) {
    throw new Error('useCellContext must be used within SettingsTable.');
  }

  return /** @type {CellContextValue<T>} */ (context);
}

/**
 * @template {object} T
 * @returns {HeaderColumnContextValue<T>}
 */
export function useHeaderColumnContext() {
  const context = useContext(HeaderColumnContext);

  if (context == null) {
    throw new Error('useHeaderColumnContext must be used within SettingsTable.');
  }

  return /** @type {HeaderColumnContextValue<T>} */ (context);
}

/**
 * @template {object} T
 * @param {SettingsTableProps<T>} props
 */
export function SettingsTable(props) {
  const state = /** @type {import('@react-stately/table').TableState<T>} */ (
    useTableState({
      ...props,
      showSelectionCheckboxes: false,
    })
  );

  const ref = useRef();
  const table = useTable(props, state, ref);

  /**
   * @param {import('@react-types/grid').GridNode<T>} node
   * @returns {Iterable<import('@react-types/grid').GridNode<T>>}
   */
  function getChildren(node) {
    return state.collection.getChildren?.(node.key) ?? [];
  }

  function renderColumnHeader() {
    return <SettingsTableColumnHeader />;
  }

  return (
    <S.Table
      {...table.gridProps}
      ref={ref}
      data-is-header-hidden={props.isHeaderHidden}
      data-is-full-width={props.isFullWidth}
    >
      <SettingsTableRowGroup type="thead">
        {state.collection.headerRows.map((headerRow) => (
          <FamilySettingsTableHeaderRow
            key={headerRow.key}
            item={headerRow}
            state={state}
            isHeaderHidden={props.isHeaderHidden}
          >
            {[...getChildren(headerRow)].map((column) => {
              return (
                <HeaderColumnContext.Provider key={column.key} value={{ column, state }}>
                  {props.renderColumnHeader
                    ? props.renderColumnHeader({ column, state })
                    : renderColumnHeader({ column, state })}
                </HeaderColumnContext.Provider>
              );
            })}
          </FamilySettingsTableHeaderRow>
        ))}
      </SettingsTableRowGroup>

      <SettingsTableRowGroup type="tbody">
        {[...getChildren(state.collection.body)].map((row) => {
          const children = [...getChildren(row)].map((cell) => {
            return (
              <CellContext.Provider key={cell.key} value={{ row, cell, state }}>
                {props.renderCell({ cell, row })}
              </CellContext.Provider>
            );
          });

          return (
            <RowContext.Provider key={row.key} value={{ row, state }}>
              <SettingsTableRow item={row} state={state}>
                {props.renderRow ? props.renderRow({ children }) : children}
              </SettingsTableRow>
            </RowContext.Provider>
          );
        })}
      </SettingsTableRowGroup>
    </S.Table>
  );
}

function S() {}
SettingsTable.S = S;

S.Table = styled.table`
  --header-height: 32px;

  &[data-is-header-hidden='true'] {
    --header-height: ${su(2)};
  }

  width: min(calc(100% - ${su(5)}), 720px);

  &[data-is-full-width='true'] {
    width: min(100%, 720px);
  }

  border-collapse: collapse;
  position: relative;
  table-layout: fixed;

  &::before {
    content: '';
    position: absolute;
    box-shadow: ${theme.boxShadow.default};
    border-radius: ${theme.borderRadius.default};
    top: var(--header-height);
    left: 0;
    bottom: 0;
    right: 0;
    z-index: -1;
  }
`;
